import React, { useState, useEffect, useRef } from "react";
import { toast } from "sonner";
import {
  Menu, X, ArrowRight, Code, BrainCircuit, Workflow, Zap, Phone, CheckCircle2, Mail, Bot, Send, Loader2, Check
} from "lucide-react";
import axios from "axios";
import { Chatbot } from "../components/Chatbot";
import { useAuth } from "../components/Auth";
import { ModeToggle } from "../components/ModeToggle";
import { ThemePicker } from "../components/ThemePicker";
import { BeamButton } from "../components/motion/BeamButton";
import { GhostButton } from "../components/motion/GhostButton";
import { Reveal } from "../components/motion/Reveal";
import { CountUp } from "../components/motion/CountUp";
import { ParticleNetwork } from "../components/motion/ParticleNetwork";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_bf547210-49b6-4258-a274-797828f214e8/artifacts/dpk5g0me_logo.png";
const HERO_BG = "https://images.unsplash.com/photo-1644088379091-d574269d422f?crop=entropy&cs=srgb&fm=jpg&ixid=M3w3NDk1ODB8MHwxfHNlYXJjaHwyfHxuZXVyYWwlMjBuZXR3b3JrfGVufDB8fHx8MTc3ODcxMzQ5OHww&ixlib=rb-4.1.0&q=85";
const SERVICES_BG = "https://images.unsplash.com/photo-1666875753105-c63a6f3bdc86?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzNzl8MHwxfHNlYXJjaHwxfHxkYXRhJTIwdmlzdWFsaXphdGlvbnxlbnwwfHx8fDE3Nzg3MTM0OTh8MA&ixlib=rb-4.1.0&q=85";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const INPUT_CLASS = "w-full bg-main/60 border border-border-dim rounded-md px-4 py-3 text-text-main placeholder:text-text-muted/70 focus:outline-none focus:ring-2 focus:ring-theme-primary focus:border-transparent focus:shadow-[0_0_0_4px_hsl(var(--theme-primary)/0.12)] transition-all";

const Navbar = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [active, setActive] = useState("home");
  const { user } = useAuth();

  const navLinks = [
    { name: "Home", href: "#home", id: "home" },
    { name: "About Us", href: "#about", id: "about" },
    { name: "Services", href: "#services", id: "services" },
    { name: "Contact", href: "#contact", id: "contact" }
  ];

  const handleLogin = () => {
    const redirectUrl = window.location.origin + '/dashboard';
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
    // REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH
  };

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const obs = new IntersectionObserver(
      (entries) => entries.forEach((e) => { if (e.isIntersecting) setActive(e.target.id); }),
      { rootMargin: "-45% 0px -50% 0px" }
    );
    ["home", "about", "services", "contact"].forEach((id) => {
      const el = document.getElementById(id);
      if (el) obs.observe(el);
    });
    return () => obs.disconnect();
  }, []);

  return (
    <nav
      data-testid="main-navbar"
      className={`fixed w-full z-40 glass border-b border-border-dim transition-all duration-300 ${scrolled ? "h-16 shadow-lg shadow-black/20" : "h-20"}`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 h-full">
        <div className="flex items-center justify-between h-full">
          <div className="flex-shrink-0 flex items-center gap-3">
            <img src={LOGO_URL} alt="305 KAOS PRODUCTIONS" className={`w-auto transition-all duration-300 ${scrolled ? "h-9" : "h-12"}`} />
          </div>
          <div className="hidden md:block">
            <div className="ml-10 flex items-center space-x-6">
              {navLinks.map((link) => (
                <a
                  key={link.name}
                  href={link.href}
                  data-testid={`nav-link-${link.id}`}
                  className={`px-2 py-2 rounded-md text-sm font-medium transition-colors ${active === link.id ? "text-theme-primary" : "text-text-muted hover:text-theme-primary"}`}
                >
                  {link.name}
                </a>
              ))}
              <ThemePicker />
              <ModeToggle />
              <BeamButton as="a" href="#contact" data-testid="nav-get-started" innerClassName="px-6 py-2 text-sm">
                Get Started
              </BeamButton>
              {user ? (
                <a href="/dashboard" data-testid="nav-dashboard-link" className="text-text-muted hover:text-theme-primary px-2 py-2 rounded-md text-sm font-medium transition-colors">
                  Dashboard
                </a>
              ) : (
                <button onClick={handleLogin} data-testid="nav-admin-login" className="text-text-muted hover:text-theme-primary px-2 py-2 rounded-md text-sm font-medium transition-colors">
                  Admin Login
                </button>
              )}
            </div>
          </div>
          <div className="-mr-2 flex md:hidden items-center gap-2">
            <ThemePicker />
            <ModeToggle />
            <button
              onClick={() => setIsOpen(!isOpen)}
              data-testid="mobile-menu-toggle"
              aria-label="Toggle navigation menu"
              className="inline-flex items-center justify-center p-2 rounded-md text-text-muted hover:text-text-main hover:bg-elevated focus:outline-none focus-visible:ring-2 focus-visible:ring-theme-primary"
            >
              {isOpen ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
            </button>
          </div>
        </div>
      </div>
      {/* Mobile menu */}
      {isOpen && (
        <div className="md:hidden glass border-t border-border-dim" data-testid="mobile-menu">
          <div className="px-2 pt-2 pb-3 space-y-1 sm:px-3">
            {navLinks.map((link) => (
              <a
                key={link.name}
                href={link.href}
                className="text-text-muted hover:text-theme-primary block px-3 py-2 rounded-md text-base font-medium"
                onClick={() => setIsOpen(false)}
              >
                {link.name}
              </a>
            ))}
            <a
              href="#contact"
              onClick={() => setIsOpen(false)}
              className="block px-3 py-2 rounded-md text-base font-bold text-theme-primary"
            >
              Get Started
            </a>
            {user ? (
              <a href="/dashboard" className="text-text-muted hover:text-theme-primary block px-3 py-2 rounded-md text-base font-medium">Dashboard</a>
            ) : (
              <button onClick={handleLogin} className="text-left w-full text-text-muted hover:text-theme-primary block px-3 py-2 rounded-md text-base font-medium">Admin Login</button>
            )}
          </div>
        </div>
      )}
    </nav>
  );
};

const ProjectModal = ({ isOpen, onClose }) => {
  const [chatMessages, setChatMessages] = useState([{ id: "greeting", role: "assistant", text: "Hello! I'm KAOS SI. What kind of app or SI integration are you looking to build?" }]);
  const [chatInput, setChatInput] = useState("");
  const [isChatLoading, setIsChatLoading] = useState(false);
  const [chatId, setChatId] = useState("");
  const chatEndRef = useRef(null);

  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen && !chatId) {
      setChatId("proj_chat_" + Math.random().toString(36).substring(2, 15));
    }
  }, [isOpen, chatId]);

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [chatMessages, isOpen]);

  const handleChatSubmit = async (e) => {
    e.preventDefault();
    if (!chatInput.trim() || isChatLoading) return;

    const userText = chatInput.trim();
    setChatInput("");
    setChatMessages(prev => [...prev, { id: crypto.randomUUID(), role: "user", text: userText }]);
    setIsChatLoading(true);

    try {
      const response = await axios.post(`${API}/chat`, {
        chat_id: chatId,
        message: userText
      });
      setChatMessages(prev => [...prev, { id: crypto.randomUUID(), role: "assistant", text: response.data.response }]);
    } catch (error) {
      setChatMessages(prev => [...prev, { id: crypto.randomUUID(), role: "assistant", text: "Connection error. Please try again." }]);
    } finally {
      setIsChatLoading(false);
    }
  };

  const handleFormSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const payload = { ...formData, chat_id: chatId };
      const response = await axios.post(`${API}/contact`, payload);
      if (response.status === 201) {
        toast.success("Message sent successfully! We'll be in touch soon.");
        setFormData({ name: "", email: "", message: "" });
        onClose();
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-[10000] flex items-center justify-center p-4 sm:p-6" data-testid="project-modal">
      <div className="absolute inset-0 bg-black/80 backdrop-blur-sm transition-opacity duration-300" onClick={onClose}></div>

      <div className="relative w-full max-w-5xl bg-surface border border-border-dim rounded-2xl shadow-2xl overflow-hidden flex flex-col md:flex-row h-[90vh] md:h-[700px] animate-in fade-in zoom-in-95 duration-200">

        <button onClick={onClose} data-testid="project-modal-close" className="absolute top-4 right-4 z-10 w-8 h-8 flex items-center justify-center bg-main/50 hover:bg-elevated text-text-muted hover:text-text-main rounded-full transition-colors border border-border-dim">
          <X className="w-5 h-5" />
        </button>

        {/* Left Side: SI Chat */}
        <div className="w-full md:w-1/2 flex flex-col border-b md:border-b-0 md:border-r border-border-dim bg-main/40 h-1/2 md:h-full">
          <div className="p-4 border-b border-border-dim bg-theme-primary/5 flex items-center gap-3 shrink-0">
            <div className="w-10 h-10 rounded-full bg-theme-primary/20 flex items-center justify-center text-theme-primary">
              <Bot className="w-6 h-6" />
            </div>
            <div>
              <h3 className="font-bold text-text-main">Project Assistant</h3>
              <p className="text-xs text-theme-primary">Powered by KAOS SI</p>
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4 space-y-4">
            {chatMessages.map((msg, idx) => (
              <div key={msg.id || idx} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
                <div className={`max-w-[85%] rounded-2xl px-4 py-2.5 text-sm leading-relaxed ${msg.role === 'user' ? 'bg-theme-primary text-black rounded-tr-sm font-medium' : 'bg-elevated text-text-main border border-border-dim rounded-tl-sm'}`}>
                  {msg.text}
                </div>
              </div>
            ))}
            {isChatLoading && (
              <div className="flex justify-start">
                <div className="bg-elevated border border-border-dim rounded-2xl rounded-tl-sm px-4 py-3 flex gap-1 items-center">
                  <div className="w-1.5 h-1.5 bg-theme-primary rounded-full animate-bounce"></div>
                  <div className="w-1.5 h-1.5 bg-theme-primary rounded-full animate-bounce delay-75"></div>
                  <div className="w-1.5 h-1.5 bg-theme-primary rounded-full animate-bounce delay-150"></div>
                </div>
              </div>
            )}
            <div ref={chatEndRef} />
          </div>

          <form onSubmit={handleChatSubmit} className="p-4 border-t border-border-dim bg-main/20 shrink-0">
            <div className="relative flex items-center">
              <input
                type="text"
                value={chatInput}
                onChange={(e) => setChatInput(e.target.value)}
                placeholder="Describe your project..."
                data-testid="project-modal-chat-input"
                className="w-full bg-main/60 border border-border-dim rounded-full py-3 pl-5 pr-12 text-sm text-text-main placeholder:text-text-muted/70 focus:outline-none focus:ring-2 focus:ring-theme-primary transition-all"
              />
              <button
                type="submit"
                disabled={isChatLoading || !chatInput.trim()}
                className="absolute right-2 w-9 h-9 rounded-full bg-theme-primary text-black flex items-center justify-center hover:opacity-90 disabled:opacity-50 transition-opacity"
              >
                <Send className="w-4 h-4" />
              </button>
            </div>
          </form>
        </div>

        {/* Right Side: Contact Form */}
        <div className="w-full md:w-1/2 flex flex-col h-1/2 md:h-full overflow-y-auto bg-surface">
          <div className="p-8 md:p-10 flex-1 flex flex-col justify-center">
            <h3 className="text-3xl font-bold mb-2 text-text-main">Let's Build It</h3>
            <p className="text-text-muted mb-8">Fill out the form below or chat with our SI to get started on your next big thing.</p>

            <form onSubmit={handleFormSubmit} className="space-y-5">
              <div>
                <label htmlFor="modal-name" className="block text-sm font-medium text-text-muted mb-2">Full Name</label>
                <input type="text" id="modal-name" required value={formData.name} onChange={(e) => setFormData({ ...formData, name: e.target.value })} className={INPUT_CLASS} placeholder="John Doe" />
              </div>
              <div>
                <label htmlFor="modal-email" className="block text-sm font-medium text-text-muted mb-2">Email Address</label>
                <input type="email" id="modal-email" required value={formData.email} onChange={(e) => setFormData({ ...formData, email: e.target.value })} className={INPUT_CLASS} placeholder="john@example.com" />
              </div>
              <div>
                <label htmlFor="modal-message" className="block text-sm font-medium text-text-muted mb-2">Project Details</label>
                <textarea id="modal-message" required rows="4" value={formData.message} onChange={(e) => setFormData({ ...formData, message: e.target.value })} className={`${INPUT_CLASS} resize-none`} placeholder="What are we building?"></textarea>
              </div>
              <BeamButton as="button" type="submit" disabled={isSubmitting} data-testid="project-modal-submit" className="w-full mt-2" innerClassName="w-full py-3.5">
                {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>) : "Submit Project"}
              </BeamButton>
            </form>
          </div>
        </div>

      </div>
    </div>
  );
};

const Hero = () => {
  const [isModalOpen, setIsModalOpen] = useState(false);

  return (
    <section id="home" className="relative min-h-screen flex items-center pt-20 overflow-hidden">
      {/* Background layers */}
      <div className="absolute inset-0 z-0">
        <img src={HERO_BG} alt="SI Background" className="w-full h-full object-cover opacity-20" />
        <div
          className="aurora-layer absolute inset-0 opacity-40 blur-3xl"
          style={{ background: "radial-gradient(40% 40% at 30% 40%, hsl(var(--theme-primary) / 0.45), transparent 70%), radial-gradient(40% 40% at 70% 60%, hsl(var(--theme-secondary) / 0.35), transparent 70%)" }}
        />
        <ParticleNetwork className="absolute inset-0 w-full h-full" />
        <div className="absolute inset-0 bg-gradient-to-b from-main/80 via-main/60 to-main"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 w-full">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
          <Reveal>
            <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full border border-theme-primary/30 bg-theme-primary/10 text-theme-primary text-sm font-medium mb-6">
              <Zap className="w-4 h-4" /> Innovating the Future
            </div>
            <h1 className="text-5xl md:text-7xl font-extrabold tracking-tight mb-6 leading-tight text-text-main">
              Next-Gen <br />
              <span className="text-gradient">App & SI</span> <br />
              Integrations
            </h1>
            <p className="text-xl text-text-muted mb-8 max-w-lg leading-relaxed">
              305 KAOS PRODUCTIONS LTD transforms businesses by bridging the gap between innovative application development and cutting-edge super intelligence.
            </p>
            <div className="flex flex-col sm:flex-row gap-4">
              <BeamButton as="button" onClick={() => setIsModalOpen(true)} data-testid="hero-start-project" innerClassName="px-8 py-4 text-lg">
                Start Your Project <ArrowRight className="w-5 h-5" />
              </BeamButton>
              <GhostButton as="a" href="#services" data-testid="hero-explore-services" className="text-lg">
                Explore Services
              </GhostButton>
            </div>
          </Reveal>

          <div className="hidden lg:flex justify-center items-center">
            <Reveal delay={0.15} className="relative w-full max-w-md">
              <div className="absolute -inset-1 rounded-3xl bg-gradient-to-tr from-theme-primary to-theme-secondary opacity-20 blur-2xl"></div>
              <img src={LOGO_URL} alt="KAOS Logo Large" className="relative w-full h-auto drop-shadow-2xl" />
            </Reveal>
          </div>
        </div>
      </div>

      <ProjectModal isOpen={isModalOpen} onClose={() => setIsModalOpen(false)} />
    </section>
  );
};

const STATS = [
  { value: 10, suffix: "+", label: "SI Models Integrated", accent: "text-theme-primary" },
  { value: 50, suffix: "+", label: "Custom Apps Deployed", accent: "text-theme-secondary" },
  { value: 99, suffix: "%", label: "Client Satisfaction", accent: "text-theme-primary" },
  { text: "24/7", label: "Dedicated Support", accent: "text-theme-secondary" }
];

const About = () => {
  return (
    <section id="about" className="py-24 relative bg-main">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-16 items-center">
          <div className="order-2 md:order-1 relative">
            <div className="absolute inset-0 bg-gradient-to-r from-theme-secondary/20 to-theme-primary/20 rounded-2xl blur-xl transform -rotate-3"></div>
            <div className="relative glass-card p-8 rounded-2xl">
              <div className="grid grid-cols-2 gap-6">
                {STATS.map((s, i) => (
                  <Reveal key={s.label} delay={i * 0.1} className="text-center p-6 border border-border-dim rounded-xl bg-main/40">
                    <h3 className={`text-4xl font-black mb-2 ${s.accent}`}>
                      {s.text ? s.text : <CountUp value={s.value} suffix={s.suffix} />}
                    </h3>
                    <p className="text-text-muted text-sm">{s.label}</p>
                  </Reveal>
                ))}
              </div>
            </div>
          </div>
          <Reveal className="order-1 md:order-2">
            <h2 className="text-sm font-bold text-theme-secondary uppercase tracking-widest mb-3">About Us</h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6 text-text-main">Pioneering Intelligent Digital Experiences</h3>
            <p className="text-text-muted text-lg mb-6 leading-relaxed">
              At 305 KAOS PRODUCTIONS LTD, we don't just build apps; we engineer intelligent ecosystems. Based at the intersection of creativity and logic, our team specializes in embedding state-of-the-art SI capabilities into scalable applications.
            </p>
            <p className="text-text-muted text-lg mb-8 leading-relaxed">
              Whether you're looking to automate complex workflows, generate dynamic content, or build predictive models, we provide end-to-end production that turns chaos into streamlined, SI-powered solutions.
            </p>
            <ul className="space-y-4">
              {[
                "Custom LLM fine-tuning & integrations",
                "Scalable microservices architecture",
                "Advanced data processing pipelines"
              ].map((item) => (
                <li key={item} className="flex items-center gap-3 text-text-main">
                  <CheckCircle2 className="w-5 h-5 text-theme-primary" />
                  {item}
                </li>
              ))}
            </ul>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Services = () => {
  const services = [
    { icon: BrainCircuit, accent: "text-theme-primary", title: "SI & ML Integrations", description: "Seamlessly integrate ChatGPT, Claude, and custom machine learning models into your existing software infrastructure to automate tasks and unlock new capabilities." },
    { icon: Code, accent: "text-theme-secondary", title: "Custom App Development", description: "End-to-end full-stack web and mobile application development with robust architectures designed for scale, speed, and exceptional user experiences." },
    { icon: Workflow, accent: "text-theme-primary", title: "Workflow Automation", description: "Replace manual, repetitive processes with intelligent, automated pipelines that connect your tools, APIs, and databases together." }
  ];

  return (
    <section id="services" className="py-24 relative overflow-hidden bg-main">
      <div className="absolute inset-0 z-0">
        <img src={SERVICES_BG} alt="Services Background" className="w-full h-full object-cover opacity-10" />
        <div className="absolute inset-0 bg-main/90"></div>
      </div>

      <div className="relative z-10 max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <Reveal className="text-center max-w-3xl mx-auto mb-16">
          <h2 className="text-sm font-bold text-theme-secondary uppercase tracking-widest mb-3">Our Services</h2>
          <h3 className="text-4xl md:text-5xl font-bold mb-6 text-text-main">What We Produce</h3>
          <p className="text-text-muted text-lg">
            We deliver high-end technical productions that transform businesses through the power of code and super intelligence.
          </p>
        </Reveal>

        <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <Reveal key={service.title} delay={index * 0.12}>
                <div className="glass-card p-8 rounded-2xl h-full transition-all duration-300 hover:-translate-y-2 hover:border-theme-primary/50 group">
                  <div className="w-16 h-16 bg-main/50 border border-border-dim rounded-xl flex items-center justify-center mb-6 transition-all duration-300 group-hover:border-theme-primary/50 group-hover:shadow-[0_0_24px_hsl(var(--theme-primary)/0.35)]">
                    <Icon className={`w-8 h-8 ${service.accent}`} />
                  </div>
                  <h4 className="text-2xl font-bold mb-4 text-text-main">{service.title}</h4>
                  <p className="text-text-muted leading-relaxed">{service.description}</p>
                </div>
              </Reveal>
            );
          })}
        </div>
      </div>
    </section>
  );
};

const Contact = () => {
  const [formData, setFormData] = useState({ name: "", email: "", message: "" });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [sent, setSent] = useState(false);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    try {
      const globalChatId = localStorage.getItem("kaos_chat_id");
      const payload = { ...formData, chat_id: globalChatId || undefined };
      const response = await axios.post(`${API}/contact`, payload);
      if (response.status === 201) {
        toast.success("Message sent successfully! We'll be in touch soon.");
        setFormData({ name: "", email: "", message: "" });
        setSent(true);
        setTimeout(() => setSent(false), 4000);
      }
    } catch (error) {
      toast.error("Failed to send message. Please try again later.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  return (
    <section id="contact" className="py-24 relative bg-main">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-16">
          <Reveal>
            <h2 className="text-sm font-bold text-theme-secondary uppercase tracking-widest mb-3">Get in Touch</h2>
            <h3 className="text-4xl md:text-5xl font-bold mb-6 text-text-main">Ready to build something chaotic?</h3>
            <p className="text-text-muted text-lg mb-10 leading-relaxed">
              Let's discuss how 305 KAOS PRODUCTIONS can integrate powerful SI and cutting-edge applications into your business.
            </p>

            <div className="space-y-8">
              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-theme-primary/10 rounded-full flex items-center justify-center border border-theme-primary/20 shrink-0">
                  <Phone className="w-5 h-5 text-theme-primary" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-1 text-text-main">Call Us</h4>
                  <a href="tel:+17546660682" data-testid="call-us-phone-link" className="text-text-muted hover:text-theme-primary transition-colors">1 (754) 666-0682</a>
                </div>
              </div>

              <div className="flex items-start gap-4">
                <div className="w-12 h-12 bg-theme-secondary/10 rounded-full flex items-center justify-center border border-theme-secondary/20 shrink-0">
                  <Mail className="w-5 h-5 text-theme-secondary" />
                </div>
                <div>
                  <h4 className="text-lg font-bold mb-1 text-text-main">Email Us</h4>
                  <p className="text-text-muted">info@kaoslabs.tech</p>
                </div>
              </div>
            </div>
          </Reveal>

          <Reveal delay={0.1}>
            <div className="glass-card p-8 md:p-10 rounded-2xl">
              <h4 className="text-2xl font-bold mb-6 text-text-main">Send a Message</h4>
              <form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-medium text-text-muted mb-2">Full Name</label>
                  <input type="text" id="name" name="name" required value={formData.name} onChange={handleChange} className={INPUT_CLASS} placeholder="John Doe" />
                </div>
                <div>
                  <label htmlFor="email" className="block text-sm font-medium text-text-muted mb-2">Email Address</label>
                  <input type="email" id="email" name="email" required value={formData.email} onChange={handleChange} className={INPUT_CLASS} placeholder="john@example.com" />
                </div>
                <div>
                  <label htmlFor="message" className="block text-sm font-medium text-text-muted mb-2">Your Message</label>
                  <textarea id="message" name="message" required rows="4" value={formData.message} onChange={handleChange} className={`${INPUT_CLASS} resize-none`} placeholder="Tell us about your project..."></textarea>
                </div>
                <BeamButton as="button" type="submit" disabled={isSubmitting} data-testid="contact-submit" className="w-full" innerClassName="w-full py-4">
                  {isSubmitting ? (<><Loader2 className="w-5 h-5 animate-spin" /> Sending...</>) : sent ? (<><Check className="w-5 h-5" /> Message Sent</>) : "Submit Message"}
                </BeamButton>
              </form>
            </div>
          </Reveal>
        </div>
      </div>
    </section>
  );
};

const Footer = () => {
  return (
    <footer className="bg-surface py-12 border-t border-border-dim">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex flex-col md:flex-row justify-between items-center gap-6">
        <div className="flex items-center gap-3">
          <img src={LOGO_URL} alt="305 KAOS PRODUCTIONS" className="h-10 w-auto" />
          <span className="font-bold tracking-wider text-sm text-text-main">305 KAOS PRODUCTIONS LTD</span>
        </div>
        <p className="text-text-muted text-sm text-center md:text-left">
          &copy; {new Date().getFullYear()} 305 KAOS PRODUCTIONS LTD. All rights reserved.
        </p>
        <div className="flex items-center gap-6">
          <a href="tel:+17546660682" data-testid="footer-phone-link" className="text-text-muted hover:text-theme-primary transition-colors text-sm">1 (754) 666-0682</a>
        </div>
      </div>
    </footer>
  );
};

export default function Home() {
  return (
    <>
      <Navbar />
      <main>
        <Hero />
        <About />
        <Services />
        <Contact />
      </main>
      <Footer />
      <Chatbot />
    </>
  );
}
